import {state} from 'lit/decorators.js';
import {html, LitElement, nothing, PropertyValues, TemplateResult} from 'lit';
import arrow from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-12_chevron-right.svg';
import './action-menu-item.js';
import {addPresentationToIcon} from '../icon/icon-accessibility.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {Messages} from '../../../@types/wishlist.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {consume} from '@lit/context';

export interface ActionItem {
  label: string | TemplateResult;
  arrow: boolean;
  callback: () => Promise<void> | void;
  trackingKey: 'size_selection' | 'remove' | 'vsp' | 'wvb' | 'hva' | 'share' | 'mix&match' | 'productline' | 'rename'
    | 'add_to_collection' | 'capsule';
}

const wait = async (ms: number) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

export abstract class ActionMenu extends LitElement {
  protected testId: string = 'ents-action-menu';

  @state()
  protected showLayer: boolean = false;

  @consume({context: i18nContext})
  protected messages!: Messages;

  protected handleLayerClosed() {
    this.updateStateOnClose();
    if (this.showLayer) {
      // this is needed because when base layer closes itself (e.g. click on 'x' or backdrop)
      // it is not automatically reflected on the 'showLayer' state
      this.showLayer = false;
    }
  }

  protected updateStateOnClose() {
    return;
  }

  protected closeLayer() {
    if (this.showLayer) {
      this.showLayer = false;
    }
  }

  protected render(): TemplateResult {
    return html`
      <ents-base-layer
        ?showLayer="${this.showLayer}"
        drawer-test-id="${this.testId}"
        @ents-layer-closed="${this.handleLayerClosed}"
        @ents-action-menu-item-clicked="${this.closeLayer}"
      >
        ${this.renderInformation()}
        <div class="ents-action-menu">
          <div class="ents-action-menu__product-banner">
            <slot name="product-banner"></slot>
          </div>
          <div class="ents-action-menu__gwp-banner">
            <slot name="gwp-banner"></slot>
          </div>
          <div class="ents-action-menu__items">
            ${this.getApplicableActionItems().map(actionItem => html`
              <ents-action-menu-item class="ents-copy"
                                     @ents-action-menu-item-clicked="${() => this.handleActionItemClick(actionItem)}">
                ${this.renderLabel(actionItem.label)} ${actionItem.arrow ? this.displayArrow() : nothing}
              </ents-action-menu-item>`)}
          </div>
        </div>
      </ents-base-layer>
    `;
  }

  shouldUpdate(changedProperties: PropertyValues): boolean {
    if (changedProperties.size === 0) {
      return false;
    }
    return super.shouldUpdate(changedProperties);
  }

  protected renderInformation(): TemplateResult | symbol {
    return nothing;
  }

  protected renderLabel(label: string | TemplateResult): TemplateResult {
    if (typeof label != 'string') {
      return label;
    }
    // Span to preserve spaces
    const unsafeLabel = unsafeHTML(`<span>${label}</span>`);
    return html`${unsafeLabel}`;
  }

  private displayArrow(): TemplateResult {
    return html`
      <div slot="arrow">${addPresentationToIcon(arrow())}</div>
    `;
  }

  private async handleActionItemClick(actionItem: ActionItem) {
    await wait(320); // base layer slide out animation takes 300ms, so we wait an extra 20ms to avoid buggy UI
    actionItem.callback();
  }

  protected getApplicableActionItems(): ActionItem[] {
    return [];
  }
}
